import { useState } from 'react';
import { Button } from './Button';
import { signIn, signUp, isAuthConfigured } from '../lib/supabase';

export interface LoginProps {
  /** Optional error message to display (e.g. from parent) */
  error?: string;
  /** Called after successful login with the user's email */
  onSuccess?: (email: string) => void;
}

export function Login({ error: externalError, onSuccess }: LoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const displayError = externalError ?? error;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    if (!isAuthConfigured()) {
      setError('Auth not configured. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to .env');
      setLoading(false);
      return;
    }

    if (isSignUp) {
      const { error: err } = await signUp(email.trim(), password);
      setLoading(false);
      if (err) {
        setError(err);
        return;
      }
      setSuccessMessage('Check your email to confirm your account, then sign in.');
      return;
    }

    const { error: err } = await signIn(email.trim(), password);
    setLoading(false);
    if (err) {
      setError(err);
      return;
    }
    onSuccess?.(email.trim());
  };

  const inputClass =
    'w-full px-4 py-3 rounded-xl bg-surface border border-white/10 text-text placeholder-text-subtle ' +
    'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors';

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-[400px]">
        <div className="rounded-3xl bg-surface border border-white/5 p-8 shadow-2xl">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-text tracking-tight">
              NoCutPoker
            </h1>
            <p className="text-text-muted mt-1.5 text-sm">
              {isSignUp ? 'Create Account' : 'Sign In'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-text text-sm font-medium mb-1.5">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                className={inputClass}
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-text text-sm font-medium mb-1.5">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                required
                minLength={6}
                className={inputClass}
                placeholder={isSignUp ? 'Min 6 characters' : '••••••••'}
              />
            </div>

            {displayError && (
              <p className="text-error text-sm" role="alert">
                {displayError}
              </p>
            )}
            {successMessage && (
              <p className="text-success text-sm" role="status">
                {successMessage}
              </p>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Please wait…' : isSignUp ? 'Sign up' : 'Sign in'}
            </Button>
          </form>

          <p className="mt-6 text-center text-text-muted text-sm">
            {isSignUp ? (
              <>
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setIsSignUp(false);
                    setError(null);
                    setSuccessMessage(null);
                  }}
                  className="text-primary font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-primary/50 rounded"
                >
                  Sign in
                </button>
              </>
            ) : (
              <>
                Don&apos;t have an account?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setIsSignUp(true);
                    setError(null);
                    setSuccessMessage(null);
                  }}
                  className="text-primary font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-primary/50 rounded"
                >
                  Sign up
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
